import { useState, useRef, useEffect } from 'react';
import { dataURLtoFile, imgSizesCalc } from 'utils';
import { IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageDeleteDialog from './ImageDeleteDialog';
import ImagePopoverOptions from './ImagePopOverOptions';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import FileUploadDialog from 'qubCommon/Files/FileUploadDialog';
import FileUploadDialog2 from 'qubCommon/Files/components/FileUploadDialog';
import { generic } from 'apis';
import { ProjectTables } from 'enums';

// makeStyle
const useStyles = makeStyles((theme) => ({
    imgClass: {
        margin: '0px auto 0'
    },
    root: {
        color: 'rgba(0, 0, 0, 0.87)',
        width: '100%',
        border: '0',
        display: 'flex',
        position: 'relative',
        fontSize: '.875rem',
        minWidth: '0',
        wordWrap: 'break-word',
        background: '#FFF',
        marginTop: '30px',
        borderRadius: '6px',
        marginBottom: '30px',
        flexDirection: 'column',
        textAlign: 'center'
    },
    input: {
        display: 'none'
    },
    inputIcon: {
        top: '-30px',
        left: '60px',
        padding: '10px!important',
        backgroundColor: '#3a404678',
        transition: 'background 1s, color 1s',
        '&:hover': {
            background: '#3a404630'
        }
    },
    imgCardRound: {
        display: 'flex!important',
        justifyContent: 'center!important',
        alignContent: 'center!important',
        borderRadius: '25px',
        boxShadow: 'unset',
        [theme.breakpoints.down('lg')]: {
            borderRadius: '25px 25px 0 0'
        }
    }
}));

const ImagePicker = ({ api, ProjectTable, recordId, recordGuid, aspect, editMode, showPairStatus = true }) => {
    const { imgWidth, imgHeight, cropWidth, cropHeight, defaultImg } = imgSizesCalc(aspect);
    const imagetouploadRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openPopOver, setOpenPopover] = useState(false);
    const [openDeleteAvatarDialog, setDeleteAvatarDialog] = useState(false);
    const [avatar, setAvatar] = useState();
    const classes = useStyles();
    const methods = useFormContext();
    const { reset, getValues } = methods;

    const { onChange, ...rest } = methods.register('avatar');
    methods.register('isAvatarDirty');

    const [defaultFiles, setDefaultFiles] = useState([]);
    const [avatarFile, setAvatarFile] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    const handlePopoverOpen = (e) => {
        setAnchorEl(anchorEl ? null : e.currentTarget);
        setOpenPopover(true);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setOpenPopover(false);
    };

    const handleDeleteAvatarDialogClose = () => {
        setDeleteAvatarDialog(false);
        setAnchorEl(null);
        setOpenPopover(false);
    };

    const handleDeleteAvatarOpen = () => {
        setDeleteAvatarDialog(true);
    };

    const handleDeleteAvatar = async () => {
        try {
            api
                ? await api.removeAvatar({
                      recordId,
                      recordGuid,
                      projectTableId: ProjectTable === ProjectTables.Person ? ProjectTables.PersonFunction : ProjectTable
                  })
                : await generic.removeAvatar({
                      recordId,
                      recordGuid,
                      projectTableId: ProjectTable === ProjectTables.Person ? ProjectTables.PersonFunction : ProjectTable
                  });
            methods.setValue('avatar', null);
            setAvatar(null);
            setDeleteAvatarDialog(false);
            setDefaultFiles([]);
            setAvatarFile(null);
            setAnchorEl(null);
            setOpenPopover(false);
        } catch {
            console.log('Error deleting avatar');
        }
    };

    // change this to useQuery
    useEffect(() => {
        const fetchData = async () => {
            try {
                const savedAvatar = api
                    ? await api.getAvatar({
                          projectTableId: ProjectTable === ProjectTables.Person ? ProjectTables.PersonFunction : ProjectTable,
                          recordId,
                          recordGuid
                      })
                    : await generic.getAvatar({
                          projectTableId: ProjectTable === ProjectTables.Person ? ProjectTables.PersonFunction : ProjectTable,
                          recordId,
                          recordGuid
                      });
                if (savedAvatar !== '') {
                    setAvatar(savedAvatar); // Set avatar to display
                    methods.setValue('isAvatarDirty', false);
                    const avatarFile = dataURLtoFile(savedAvatar, 'Avatar'); // Convert from base64 to File for avatar input in form
                    reset({
                        ...getValues(),
                        avatar: avatarFile
                    });
                    setDefaultFiles([avatarFile]);
                    setAvatarFile(avatarFile);
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, [api, ProjectTable, recordId, recordGuid, reset, getValues]);

    const handleSaveImage = (files) => {
        if (files && files.length > 0) {
            const myFile = files[0].file;
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataURL = reader.result;
                const avatarFile = dataURLtoFile(dataURL, 'avatar.jpg');
                imagetouploadRef.current = avatarFile;
                setAvatar(dataURL);
                methods.setValue('avatar', imagetouploadRef.current);
                methods.setValue('isAvatarDirty', true);
            };
            reader.readAsDataURL(myFile);
            setDefaultFiles(files.map((file) => file.file));
            setAvatarFile(files[0].file);
            setAnchorEl(null);
            setOpenPopover(false);
        }
    };

    return (
        <div>
            <div className={classes.imgCardRound}>
                <div className={classes.root}>
                    <div className={classes.imgClass}>
                        {!avatar ? (
                            <img
                                src={defaultImg}
                                alt="user"
                                style={{
                                    width: `${imgWidth}px`,
                                    height: `${imgHeight}px`,
                                    borderRadius: '5px'
                                }}
                            />
                        ) : (
                            <img
                                src={avatar}
                                alt="user"
                                style={{
                                    width: `${imgWidth}px`,
                                    height: `${imgHeight}px`,
                                    borderRadius: '5px'
                                }}
                            />
                        )}

                        {!avatar ? (
                            <div>
                                {editMode && (
                                    <IconButton color="primary" onClick={() => setOpenDialog(true)} className={classes.inputIcon}>
                                        <PhotoCameraIcon style={{ color: 'white' }} />
                                    </IconButton>
                                )}
                            </div>
                        ) : (
                            <>
                                {editMode && (
                                    <IconButton color="primary" className={classes.inputIcon} onClick={(e) => handlePopoverOpen(e)}>
                                        <PhotoCameraIcon style={{ color: 'white' }} />
                                    </IconButton>
                                )}
                                <ImagePopoverOptions
                                    openPopOver={openPopOver}
                                    anchorEl={anchorEl}
                                    setOpenDialog={setOpenDialog}
                                    handlePopoverClose={handlePopoverClose}
                                    handleDeleteAvatarOpen={handleDeleteAvatarOpen}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            {/* <FileUploadDialog
                open={openDialog}
                setOpen={setOpenDialog}
                defaultFiles={defaultFiles}
                onSave={handleSaveImage}
                limit={1}
                aspectRatio={aspect}
                fixedAspectRatio
                imageOnly
            /> */}
            <FileUploadDialog2
                open={openDialog}
                setOpen={setOpenDialog}
                avatar
                avatarFile={avatarFile}
                onSave={handleSaveImage}
                onDelete={handleDeleteAvatar}
                limit={1}
                fixedAspectRatio
                aspectRatio={aspect}
                showPairStatus={showPairStatus}
            />
            <ImageDeleteDialog
                open={openDeleteAvatarDialog}
                handleDeleteAvatarDialogClose={handleDeleteAvatarDialogClose}
                handleDeleteAvatar={handleDeleteAvatar}
            />
        </div>
    );
};

ImagePicker.propTypes = {
    api: PropTypes.object,
    recordGuid: PropTypes.string,
    aspect: PropTypes.number
};

export default ImagePicker;
